import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { usePortal } from "../../../../context/PortalsContext";
import {
  AssignmentDependency,
  AssignmentType,
  DdlAssignment,
  PackageType,
} from "../../../../services/api/types/assignments/assignment";
import { brand, error, gray } from "../../../../theme/themePrimitives";
import { ChangeEventType, FormErrors, FormValues } from "../AssignmentUploads";
import { ReadingExperienceSection } from "./ReadingExperienceSection";

interface MainAdminFormProps {
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: FormErrors;
  handleChange: (e: ChangeEventType) => void;
  handleSave: () => void;
  buttonLoading: boolean;
  isEditMode: boolean;
  isCustom: Boolean;
  dependencyOptions: DdlAssignment[];
}

const assignmentTypes: { name: string; value: AssignmentType }[] = [
  { name: "Course", value: AssignmentType.COURSE },
  { name: "Survey", value: AssignmentType.SURVEY },
  { name: "Policy", value: AssignmentType.POLICY },
];

const packageTypes: { name: string; value: PackageType }[] = [
  { name: "SCORM 2004", value: PackageType.Scorm2004 },
  { name: "SCORM 1.2", value: PackageType.Scorm12 },
  { name: "Gomo Wrapper SCORM 1.2", value: PackageType.GomoScorm12 },
  { name: "Gomo Wrapper SCORM 2004", value: PackageType.GomoScorm2004 },
  { name: "xApi", value: PackageType.xApi },
];

const pdfPackageType: { name: string; value: PackageType } = {
  name: "PDF Uploads",
  value: PackageType.PdfUpload,
};

const videoPackageType = {
  name: "Video Uploads",
  value: PackageType.VideoUpload,
};

export const MainAdminForm: React.FC<MainAdminFormProps> = ({
  formValues,
  errors,
  handleChange,
  handleSave,
  setFormValues,
  buttonLoading,
  isEditMode,
  isCustom,
  dependencyOptions,
}) => {
  const { portals } = usePortal();

  // Show toggle for custom assignments in non-main portals
  // Show for all non-main portals (assignments created there are typically custom)
  const showSubPortalsToggle =
    !portals.selectedPortal?.isMain && !portals.selectedPortal?.isSubPortal;

  const isCourseAssignmentType =
    formValues.assignmentType === AssignmentType.COURSE;
  const isPolicyAssignmentType =
    formValues.assignmentType === AssignmentType.POLICY;

  const availablePackageTypes = isPolicyAssignmentType
    ? [pdfPackageType]
    : isCourseAssignmentType
      ? [...packageTypes, pdfPackageType, videoPackageType]
      : packageTypes;

  const isPdfUpload = formValues.packageType === PackageType.PdfUpload;
  const isVideoUpload = formValues.packageType === PackageType.VideoUpload;

  // Keep the selected package type in sync with what's available for the
  // current assignment type: auto-select the sole PDF option for Policy,
  // and clear a now-unavailable selection for the other types.
  useEffect(() => {
    if (isPolicyAssignmentType) {
      if (!isPdfUpload) {
        setFormValues((prev) => ({
          ...prev,
          packageType: PackageType.PdfUpload,
        }));
      }
      return;
    }
    if (
      isPdfUpload &&
      !availablePackageTypes.some((p) => p.value === formValues.packageType)
    ) {
      setFormValues((prev) => ({
        ...prev,
        packageType: undefined,
        pageByPage: false,
        acknowledgement: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.assignmentType]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 4 }}>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Assignment type
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Select
              disabled={isEditMode}
              name="assignmentType"
              IconComponent={ExpandMoreIcon}
              value={formValues.assignmentType || ""}
              error={Boolean(errors.assignmentType)}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) =>
                selected ? (
                  assignmentTypes.find((t) => t.value === selected)?.name
                ) : (
                  <Typography color="text.disabled">
                    Select assignment type
                  </Typography>
                )
              }
              sx={{
                borderRadius: "8px",
                minWidth: "150px",
                backgroundColor: "transparent",
                height: "44px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
            >
              {assignmentTypes.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
            {errors.assignmentType && (
              <Typography
                sx={{ margin: "4px 4px 0px !important" }}
                fontSize={"12px"}
                color={error[600]}
              >
                {errors.assignmentType}
              </Typography>
            )}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Package type
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <Select
              disabled={isEditMode}
              name="packageType"
              IconComponent={ExpandMoreIcon}
              value={formValues.packageType || ""}
              error={Boolean(errors.packageType)}
              onChange={handleChange}
              displayEmpty
              renderValue={(selected) =>
                selected ? (
                  availablePackageTypes.find((t) => t.value === selected)?.name
                ) : (
                  <Typography color="text.disabled">
                    Select package type
                  </Typography>
                )
              }
              sx={{
                borderRadius: "8px",
                minWidth: "150px",
                backgroundColor: "transparent",
                height: "44px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
            >
              {availablePackageTypes.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            {errors.packageType && (
              <Typography
                sx={{ margin: "4px 4px 0px !important" }}
                fontSize={"12px"}
                color={error[600]}
              >
                {errors.packageType}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              SKU
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="sku"
              name="SKU"
              placeholder="Enter SKU"
              type="text"
              value={formValues.SKU}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              {...(!isCustom
                ? { error: Boolean(errors.SKU), helperText: errors.SKU }
                : {})}
              slotProps={{
                input: {
                  endAdornment: !isCustom && errors.SKU && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Code
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="code"
              name="code"
              placeholder="Enter code"
              type="text"
              value={formValues.code}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.code)}
              helperText={errors.code}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.code && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
              }}
            />
          </Stack>
        </Stack>
      </Box>
      {(isPdfUpload || isVideoUpload) && (
        <ReadingExperienceSection
          formValues={formValues}
          setFormValues={setFormValues}
          isPdfUpload={isPdfUpload}
          isVideoUpload={isVideoUpload}
        />
      )}
      <Box sx={{ display: "flex", gap: 4, mt: 2 }}>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Primary title
              <Box component="span" sx={{ color: `var(--error-700)` }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              id="title"
              name="title"
              placeholder="Enter primary title"
              type="text"
              value={formValues.title}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.title)}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.title && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Thumbnail title</Typography>
            <TextField
              fullWidth
              id="titleShort"
              name="titleShort"
              placeholder="Enter thumbnail title"
              type="text"
              value={formValues.titleShort}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.titleShort)}
              helperText={errors.titleShort}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
              slotProps={{
                input: {
                  endAdornment: errors.titleShort ? (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ) : (
                    <Tooltip
                      title="Displayed on assignment cards. Defaults to Primary Title if left empty."
                      arrow
                      placement="right"
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: gray[800],
                          },
                        },
                        arrow: {
                          sx: {
                            color: gray[800],
                          },
                        },
                      }}
                    >
                      <HelpOutlineOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "#98A2B3",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ),
                },
              }}
            />
          </Stack>
          {showSubPortalsToggle && (
            <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.isAvailableToSubportals}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        isAvailableToSubportals: e.target.checked,
                      }))
                    }
                  />
                }
                label="Available to subportals"
                sx={{
                  marginLeft: 0,
                  gap: "1rem",
                  "& .MuiFormControlLabel-label": {
                    color: "black",
                  },
                }}
              />
            </Box>
          )}
        </Stack>
        <Stack spacing={3} width={"100%"}>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2">Dependency</Typography>
              <Tooltip
                title="Defines assignment dependencies."
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: gray[800],
                    },
                  },
                  arrow: {
                    sx: {
                      color: gray[800],
                    },
                  },
                }}
              >
                <HelpOutlineOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#98A2B3",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Box>
            <Autocomplete<DdlAssignment, true>
              multiple
              filterSelectedOptions
              options={dependencyOptions}
              value={dependencyOptions.filter((o) =>
                formValues.dependency.some(
                  (d) => d.prerequisiteAssignmentUid === o.AssignmentUid,
                ),
              )}
              size="small"
              isOptionEqualToValue={(a, b) =>
                a.AssignmentUid === b.AssignmentUid
              }
              getOptionLabel={(o) => o.title}
              onChange={(_, newValue) =>
                setFormValues((prev) => ({
                  ...prev,
                  dependency: newValue.map(
                    (o): AssignmentDependency => ({
                      prerequisiteAssignmentUid: o.AssignmentUid,
                      prerequisiteAssignmentId: o.AssignmentId,
                    }),
                  ),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select assignments"
                  error={Boolean(errors.dependency)}
                  helperText={errors.dependency}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              )}
              renderValue={(value, getTagProps) => (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
                >
                  {value.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={option.title}
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              slotProps={{
                popupIndicator: { sx: { border: "none" } },
                clearIndicator: { sx: { display: "none" } },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                color: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? gray[400] : "black"}`,
              }}
            >
              Max no. attempts
            </Typography>
            <TextField
              placeholder="Select no. attempts"
              type="number"
              disabled={
                formValues.packageType === PackageType.PdfUpload ||
                formValues.packageType === PackageType.VideoUpload
              }
              value={formValues.maxAttempts}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^0-9]/g, "");
                setFormValues((prev) => ({
                  ...prev,
                  maxAttempts: sanitized === "" ? undefined : Number(sanitized),
                }));
              }}
              onKeyDown={(e) =>
                ["-", "+", "e", "E", "."].includes(e.key) && e.preventDefault()
              }
              slotProps={{ htmlInput: { min: 0 } }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  px: "14px",
                  py: "10px",
                  cursor: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? "not-allowed" : "text"}`,
                },
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography>Tags</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formValues.tags}
              onChange={(_, newValue) =>
                setFormValues((prev) => ({ ...prev, tags: newValue }))
              }
              renderValue={(value: string[], getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "6px",
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={Boolean(errors.tags)}
                  helperText={errors.tags}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </Box>
      <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
        {!isEditMode && (
          <>
            {buttonLoading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={handleSave}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                  "&:hover": {
                    bgcolor: `${brand[700]} !important`,
                  },
                }}
              >
                Continue
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
